import { useEffect, useReducer, useMemo, useCallback } from 'react';
import { RRule, rrulestr } from 'rrule';
import { Form, Row, Col, Card, Container } from 'react-bootstrap';

import SourcesHelper from '../../../helpers/features/SourcesHelper';
import { formatDate, getOrdinalLabel, getMonthName, isValidDay } from '../../../utils/rrule';
import { capitalizeFirstLetter } from '../../../utils/common';
import { initialState, rruleReducer } from '../../../store/reducers/rrule';

const { weekdays, frequencyUnits, bysetposOptions, freqOptions } = SourcesHelper;

const CreateSourceScheduler = ({ formProps, frequency }) => {
  const [state, dispatch] = useReducer(rruleReducer, initialState);
  const {
    startDate,
    freq,
    interval,
    byweekday,
    bymonthday,
    bysetpos,
    endMode,
    endDate,
    monthlyMode,
    yearlyMode,
    bymonth,
    dayError,
    intervalError,
  } = state;

  const { rruleString, humanReadableText } = useMemo(() => {
    const isDaySelectionMode =
      (freq === RRule.MONTHLY && monthlyMode === 'dayOfMonth') ||
      (freq === RRule.YEARLY && yearlyMode === 'dayOfMonth');
    if (isDaySelectionMode && !isValidDay(bymonthday, bymonth, freq)) {
      return { rruleString: '', humanReadableText: 'Invalid day for selected month' };
    }

    if (!interval || interval < 1) {
      return { rruleString: '', humanReadableText: 'Invalid interval' };
    }

    const options = {
      freq,
      interval: freq === RRule.YEARLY ? 1 : interval,
      dtstart: startDate,
    };

    if (freq === RRule.WEEKLY) {
      if (byweekday.length > 0) options.byweekday = byweekday;
    } else if (freq === RRule.MONTHLY) {
      if (monthlyMode === 'dayOfMonth') {
        options.bymonthday = bymonthday;
      } else {
        options.byweekday = byweekday.length > 0 ? [byweekday[0]] : undefined;
        options.bysetpos = bysetpos;
      }
    } else if (freq === RRule.YEARLY) {
      options.bymonth = bymonth;
      if (yearlyMode === 'dayOfMonth') {
        options.bymonthday = bymonthday;
      } else {
        options.byweekday = byweekday.length > 0 ? [byweekday[0]] : undefined;
        options.bysetpos = bysetpos;
      }
    }

    if (endMode === 'on') {
      options.until = endDate;
    }

    try {
      const rule = new RRule(options);
      let text = rule.toText();

      const hasSetpos =
        bysetpos.length > 0 &&
        ((freq === RRule.YEARLY && yearlyMode === 'dayOfWeek') ||
          (freq === RRule.MONTHLY && monthlyMode === 'dayOfWeek'));
      if (hasSetpos) {
        const ordinal = getOrdinalLabel(bysetpos[0]);
        const parts = text.split(' on ');
        text = `${parts[0]} on the ${ordinal} ${parts[1]}`;
      }

      return { rruleString: rule.toString(), humanReadableText: text };
    } catch (e) {
      console.error('Error creating RRule:', e);
      return { rruleString: '', humanReadableText: 'Error generating rule' };
    }
  }, [
    startDate,
    freq,
    interval,
    byweekday,
    bymonthday,
    bysetpos,
    endMode,
    endDate,
    monthlyMode,
    yearlyMode,
    bymonth,
  ]);

  const setField = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value });
  }, []);

  const handleIntervalChange = useCallback(value => {
    dispatch({ type: 'SET_INTERVAL', value });
  }, []);

  const handleWeekdayChange = useCallback(weekdayValue => {
    dispatch({ type: 'SET_BYWEEKDAY', value: weekdayValue });
  }, []);

  const handleBymonthdayChange = useCallback(dayStr => {
    dispatch({ type: 'SET_BYMONTHDAY', value: parseInt(dayStr, 10) });
  }, []);

  useEffect(() => {
    if (formProps?.setFieldValue) {
      formProps.setFieldValue('frequency', rruleString);
    }
  }, [rruleString]);

  useEffect(() => {
    if (frequency) {
      try {
        // Use rrulestr to parse the string into an RRule object
        const rule = rrulestr(frequency);
        const { options } = rule;

        // Dispatch actions to update the state with the parsed options
        if (options.dtstart) {
          dispatch({ type: 'SET_FIELD', field: 'startDate', value: options.dtstart });
        }
        if (typeof options.freq === 'number') {
          dispatch({ type: 'SET_FIELD', field: 'freq', value: options.freq });
        }
        if (typeof options.interval === 'number') {
          dispatch({ type: 'SET_FIELD', field: 'interval', value: options.interval });
        }
        if (options.byweekday) {
          const list = Array.isArray(options.byweekday) ? options.byweekday : [options.byweekday];
          const ds = list.map(day => (typeof day === 'number' ? day : day.weekday));
          dispatch({ type: 'SET_FIELD', field: 'byweekday', value: ds });
        }
        if (options.bymonthday) {
          const monthday = Array.isArray(options.bymonthday)
            ? options.bymonthday[0]
            : options.bymonthday;
          dispatch({ type: 'SET_FIELD', field: 'bymonthday', value: monthday });
        }
        if (options.bysetpos) {
          const setpos = Array.isArray(options.bysetpos) ? options.bysetpos : [options.bysetpos];
          dispatch({ type: 'SET_FIELD', field: 'bysetpos', value: setpos });
        }
        if (options.until) {
          dispatch({ type: 'SET_FIELD', field: 'endMode', value: 'on' });
          dispatch({ type: 'SET_FIELD', field: 'endDate', value: options.until });
        } else {
          dispatch({ type: 'SET_FIELD', field: 'endMode', value: 'never' });
        }
        if (options.bymonth) {
          const month = Array.isArray(options.bymonth) ? options.bymonth[0] : options.bymonth;
          dispatch({ type: 'SET_FIELD', field: 'bymonth', value: month });
        }

        // Set the monthly/yearly mode based on the presence of bysetpos
        if (options.freq === RRule.MONTHLY) {
          const mode = options.bysetpos && options.bysetpos.length > 0 ? 'dayOfWeek' : 'dayOfMonth';
          dispatch({ type: 'SET_FIELD', field: 'monthlyMode', value: mode });
        }
        if (options.freq === RRule.YEARLY) {
          const mode = options.bysetpos && options.bysetpos.length > 0 ? 'dayOfWeek' : 'dayOfMonth';
          dispatch({ type: 'SET_FIELD', field: 'yearlyMode', value: mode });
        }
      } catch (e) {
        console.error('Error parsing frequency RRULE string:', e);
        // Optionally, reset to a default state if the string is invalid
        // dispatch({ type: 'RESET_STATE' });
      }
    }
  }, [frequency]);

  const renderFrequencyOptions = useCallback(() => {
    switch (freq) {
      case RRule.WEEKLY:
        return (
          <Form.Group className="mt-3">
            <Form.Label>Repeat on</Form.Label>
            <div>
              {weekdays.map(day => (
                <Form.Check
                  inline
                  key={day.label}
                  type="checkbox"
                  id={`weekday-${day.label}`}
                  label={day.label}
                  checked={byweekday.includes(day.value.weekday)}
                  onChange={() => handleWeekdayChange(day.value.weekday)}
                />
              ))}
            </div>
          </Form.Group>
        );
      case RRule.MONTHLY:
        return (
          <div className="mt-3">
            <Form.Check
              type="radio"
              id="monthly-day-of-month"
              label="On day"
              checked={monthlyMode === 'dayOfMonth'}
              onChange={() => setField('monthlyMode', 'dayOfMonth')}
            />
            {monthlyMode === 'dayOfMonth' && (
              <div className="ms-4 mt-2">
                <Form.Control
                  type="number"
                  value={bymonthday}
                  className="w-25"
                  onChange={e => handleBymonthdayChange(e.target.value)}
                  isInvalid={!!dayError}
                />
                <Form.Control.Feedback type="invalid">{dayError}</Form.Control.Feedback>
              </div>
            )}

            <Form.Check
              className="mt-2"
              type="radio"
              id="monthly-day-of-week"
              label="On the"
              checked={monthlyMode === 'dayOfWeek'}
              onChange={() => setField('monthlyMode', 'dayOfWeek')}
            />
            {monthlyMode === 'dayOfWeek' && (
              <Row className="ms-4 mt-2">
                <Col sm={4}>
                  <Form.Select
                    value={bysetpos[0] || 1}
                    onChange={e => setField('bysetpos', [parseInt(e.target.value, 10)])}
                  >
                    {bysetposOptions.map(option => (
                      <option value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col sm={5}>
                  <Form.Select
                    value={byweekday[0] ?? weekdays[1].value.weekday}
                    onChange={e => setField('byweekday', [parseInt(e.target.value, 10)])}
                  >
                    {weekdays.map(day => (
                      <option key={day.label} value={day.value.weekday}>
                        {day.label}day
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            )}
          </div>
        );
      case RRule.YEARLY:
        return (
          <div className="mt-3">
            <Row className="align-items-center">
              <Col sm={6}>
                <Form.Label>Month</Form.Label>
                <Form.Select
                  value={bymonth}
                  onChange={e => setField('bymonth', parseInt(e.target.value, 10))}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {getMonthName(i + 1)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Form.Check
              className="mt-3"
              type="radio"
              id="yearly-day-of-month"
              label="On day"
              checked={yearlyMode === 'dayOfMonth'}
              onChange={() => setField('yearlyMode', 'dayOfMonth')}
            />
            {yearlyMode === 'dayOfMonth' && (
              <div className="ms-4 mt-2">
                <Form.Control
                  type="number"
                  value={bymonthday}
                  className="w-25"
                  onChange={e => handleBymonthdayChange(e.target.value)}
                  isInvalid={!!dayError}
                />
                <Form.Control.Feedback type="invalid">{dayError}</Form.Control.Feedback>
              </div>
            )}
            <Form.Check
              className="mt-2"
              type="radio"
              id="yearly-day-of-week"
              label="On the"
              checked={yearlyMode === 'dayOfWeek'}
              onChange={() => setField('yearlyMode', 'dayOfWeek')}
            />
            {yearlyMode === 'dayOfWeek' && (
              <Row className="ms-4 mt-2">
                <Col sm={4}>
                  <Form.Select
                    value={bysetpos[0] || 1}
                    onChange={e => setField('bysetpos', [parseInt(e.target.value, 10)])}
                  >
                    {bysetposOptions.map(option => (
                      <option value={option.value}>{option.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col sm={5}>
                  <Form.Select
                    value={byweekday[0] ?? weekdays[1].value.weekday}
                    onChange={e => setField('byweekday', [parseInt(e.target.value, 10)])}
                  >
                    {weekdays.map(day => (
                      <option key={day.label} value={day.value.weekday}>
                        {day.label}day
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            )}
          </div>
        );
      default:
        return null;
    }
  }, [
    freq,
    byweekday,
    monthlyMode,
    yearlyMode,
    bymonthday,
    bysetpos,
    dayError,
    handleWeekdayChange,
    handleBymonthdayChange,
    setField,
  ]);

  return (
    <Container className="py-4">
      <Card>
        <Card.Body>
          <Form>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={2}>
                Starts on
              </Form.Label>
              <Col sm={4}>
                <Form.Control
                  type="date"
                  value={formatDate(startDate)}
                  onChange={e => setField('startDate', new Date(e.target.value))}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3 align-items-center">
              <Form.Label column sm={2}>
                Repeat
              </Form.Label>
              <Col sm={4}>
                <Form.Select value={freq} onChange={e => setField('freq', Number(e.target.value))}>
                  {freqOptions.map(option => (
                    <option value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Form.Label column sm={2} className="text-sm-center">
                every
              </Form.Label>
              <Col sm={2}>
                <Form.Control
                  type="number"
                  value={freq === RRule.YEARLY ? 1 : interval}
                  min={1}
                  onChange={e => handleIntervalChange(parseInt(e.target.value, 10))}
                  disabled={freq === RRule.YEARLY}
                  isInvalid={!!intervalError}
                />
                <Form.Control.Feedback type="invalid">{intervalError}</Form.Control.Feedback>
              </Col>
              <Form.Label column sm={2}>
                {frequencyUnits[freq]}
              </Form.Label>
            </Form.Group>

            {renderFrequencyOptions()}

            <Form.Group as={Row} className="mt-4">
              <Form.Label column sm={2}>
                Ends
              </Form.Label>
              <Col sm={10}>
                <Form.Check
                  type="radio"
                  id="ends-never"
                  label="Never"
                  checked={endMode === 'never'}
                  onChange={() => setField('endMode', 'never')}
                />
                <Row className="align-items-center mt-2">
                  <Col xs="auto">
                    <Form.Check
                      type="radio"
                      id="ends-on"
                      label="On"
                      checked={endMode === 'on'}
                      onChange={() => setField('endMode', 'on')}
                    />
                  </Col>
                  <Col xs={4}>
                    <Form.Control
                      type="date"
                      value={formatDate(endDate)}
                      onChange={e => setField('endDate', new Date(e.target.value))}
                      disabled={endMode !== 'on'}
                    />
                  </Col>
                </Row>
              </Col>
            </Form.Group>
          </Form>
        </Card.Body>
        <Card.Footer className="bg-light">
          <p>
            <strong>Rule:</strong>{' '}
            <span className={dayError ? 'text-danger' : 'text-success'}>{humanReadableText}</span>
          </p>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default CreateSourceScheduler;

export const ruleToText = rruleString => {
  try {
    const rule = rrulestr(rruleString);
    const { options } = rule;
    let { yearlyMode, monthlyMode, freq, bysetpos } = initialState;
    if (typeof options.freq === 'number') {
      freq = options.freq;
    }
    if (options.bysetpos) {
      bysetpos = Array.isArray(options.bysetpos) ? options.bysetpos : [options.bysetpos];
    }
    if (options.freq === RRule.MONTHLY) {
      monthlyMode = options.bysetpos && options.bysetpos.length > 0 ? 'dayOfWeek' : 'dayOfMonth';
    }
    if (options.freq === RRule.YEARLY) {
      yearlyMode = options.bysetpos && options.bysetpos.length > 0 ? 'dayOfWeek' : 'dayOfMonth';
    }

    let text = rule.toText();

    const hasSetpos =
      bysetpos.length > 0 &&
      ((freq === RRule.YEARLY && yearlyMode === 'dayOfWeek') ||
        (freq === RRule.MONTHLY && monthlyMode === 'dayOfWeek'));
    if (hasSetpos) {
      const ordinal = getOrdinalLabel(bysetpos[0]);
      const parts = text.split(' on ');
      text = `${parts[0]} on the ${ordinal} ${parts[1]}`;
    }

    return capitalizeFirstLetter(text);
  } catch (e) {
    console.error('Error converting RRULE to text:', e);
    return 'Invalid rule';
  }
};
